import { z } from 'zod';

// Metric definitions shared between frontend and backend
export const METRIC_DEFINITIONS = [
  { key: 'staff_contacted_over_3_days', label: 'Staff contacted', category: 'Staff Metrics' },
  {
    key: 'total_outbound_calls',
    label: 'Total number of outbound calls made',
    category: 'Staff Metrics',
  },
  { key: 'total_inbound_calls', label: 'Total Inbound calls received', category: 'Staff Metrics' },
  {
    key: 'calls_with_availability_slots',
    label: 'Calls with availability slots',
    category: 'Staff Metrics',
  },
  {
    key: 'total_hours_captured_from_availability',
    label: 'Total Hours Captured from Availability',
    category: 'Staff Metrics',
  },
  {
    key: 'confirmed_availability_calls',
    label: 'Calls with availability slots (confirmed)',
    category: 'Staff Metrics',
  },
  {
    key: 'unconfirmed_availability_calls',
    label: 'Calls with availability slots (unconfirmed)',
    category: 'Staff Metrics',
  },
  {
    key: 'confirmed_hours_from_availability',
    label: 'Total Hours Captured from Availability (confirmed)',
    category: 'Staff Metrics',
  },
  {
    key: 'unconfirmed_hours_from_availability',
    label: 'Total Hours Captured from Availability (unconfirmed)',
    category: 'Staff Metrics',
  },
  {
    key: 'total_hours_from_lead_generation_email',
    label: 'Total Hours from Lead Generation Email',
    category: 'Staff Metrics',
  },
  {
    key: 'total_hours_from_newcross_callbacks',
    label: 'Total Hours from Newcross Call Backs',
    category: 'Staff Metrics',
  },
  {
    key: 'staff_provided_future_consent_count',
    label: 'Staff who provided future consent',
    category: 'Consent Metrics',
  },
  {
    key: 'staff_declined_future_consent_count',
    label: 'Staff who declined future consent',
    category: 'Consent Metrics',
  },
  {
    key: 'calls_with_availability_captured',
    label: 'Calls with availability captured',
    category: 'Call Performance',
  },
  { key: 'calls_with_voicemail', label: 'Calls with voicemail', category: 'Call Performance' },
  {
    key: 'pickup_rate_all_calls',
    label: 'Pick-up rate (all qualifying calls)',
    category: 'Call Performance',
  },
  {
    key: 'calls_under_22_seconds',
    label: 'Calls under 22 seconds removed',
    category: 'Call Performance',
  },
  {
    key: 'pickup_rate_excluding_under_22_seconds',
    label: 'Pick-up rate (excluding calls under 22 seconds)',
    category: 'Call Performance',
  },
  {
    key: 'calls_picked_up_1st_attempt',
    label: 'Calls picked up on 1st attempt',
    category: 'Call Performance',
  },
  {
    key: 'calls_picked_up_2nd_attempt',
    label: 'Calls picked up on 2nd attempt',
    category: 'Call Performance',
  },
] as const;

export type MetricDefinition = (typeof METRIC_DEFINITIONS)[number];

// Report data entry schema (single metric in the data array)
export const reportDataEntrySchema = z.object({
  key: z.string(),
  value: z.number().nullable(),
  human_reviewed_value: z.number().nullable(),
  label: z.string(),
  category: z.string(),
});

export type ReportDataEntry = z.infer<typeof reportDataEntrySchema>;

// Report record schema
export const reportRecordSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  week_start_date: z.string().or(z.date()),
  week_end_date: z.string().or(z.date()),
  campaign_id: z.string().uuid().nullable(),
  campaign_name: z.string().optional(),
  data: z.array(reportDataEntrySchema),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
});

export type ReportRecord = z.infer<typeof reportRecordSchema>;

// Call comment schema for PDF generation (not stored in DB)
export const reportCallCommentSchema = z.object({
  id: z.string().uuid(),
  call_id: z.string().uuid(),
  content: z.string(),
  created_at: z.string().datetime(),
  user_name: z.string().nullable(),
  contact_name: z.string().nullable(),
  contact_phone: z.string().nullable(),
});

export type ReportCallComment = z.infer<typeof reportCallCommentSchema>;

// Create report DTO
export const createReportDtoSchema = z
  .object({
    name: z.string().min(1, 'Report name is required'),
    week_start_date: z.coerce.date().optional(),
    week_end_date: z.coerce.date().optional(),
    campaign_id: z.string().uuid().optional(),
    total_hours_from_lead_generation_email: z.coerce.number().min(0).optional(),
    total_hours_from_newcross_callbacks: z.coerce.number().min(0).optional(),
    selected_metrics: z.array(z.string()).optional(),
  })
  .refine(
    (data) => {
      // Either campaign_id OR both dates must be provided
      const hasCampaign = !!data.campaign_id;
      const hasDates = !!data.week_start_date && !!data.week_end_date;
      return hasCampaign || hasDates;
    },
    {
      message: 'Either campaign_id or both week_start_date and week_end_date must be provided',
    },
  );

export type CreateReportDto = z.infer<typeof createReportDtoSchema>;

// Report query schema for pagination and filtering
export const reportQuerySchema = z.object({
  // Pagination
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(10),

  // Date range filter
  weekStartFrom: z.coerce.date().optional(),
  weekStartTo: z.coerce.date().optional(),

  // Campaign filter
  campaign_id: z.string().uuid().optional(),

  // Sorting
  sortBy: z.enum(['week_start_date', 'created_at']).default('week_start_date'),
  sortOrder: z.enum(['asc', 'desc']).default('desc'),
});

export type ReportQuery = z.infer<typeof reportQuerySchema>;
